import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from '../Modal';
import StaffMultiPicker from '../common/StaffMultiPicker';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreateReminderDto,
  ReminderType,
  ReminderPriority,
  EntityType,
  handleApiError
} from '../../services/reminderService';
import reminderApi from '../../services/reminderService';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  
  span {
    color: var(--error-color);
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
  
  &.error {
    border-color: var(--error-color);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    
    &:hover {
      background: var(--primary-light); color: var(--primary-color);
    }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--hover-background);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
`;

const HelpText = styled.p`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
`;

interface CreateReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  prefilledEntityType?: EntityType;
  prefilledEntityId?: number;
}

const CreateReminderModal: React.FC<CreateReminderModalProps> = ({
  isOpen,
  onClose,
  onSave,
  prefilledEntityType,
  prefilledEntityId
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<CreateReminderDto>({
    assignedUserIds: user?.id ? [user.id] : [],
    reminderType: ReminderType.Call,
    title: '',
    description: '',
    dueDateTime: '',
    entityType: prefilledEntityType,
    entityId: prefilledEntityId,
    priority: ReminderPriority.Medium,
    isRecurring: false,
    recurrencePattern: undefined
  });

  // ברירת מחדל בכל פתיחה: המשתמש הנוכחי (המודאל נשאר mounted בין פתיחות בחלק מהמסכים)
  const currentUserId = user?.id;
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, assignedUserIds: currentUserId ? [currentUserId] : [] }));
    }
  }, [isOpen, currentUserId]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'כותרת היא שדה חובה';
    }

    if (formData.assignedUserIds.length === 0) {
      newErrors.assignedUserIds = 'יש לבחור לפחות עובד אחד';
    }

    if (!formData.dueDateTime) {
      newErrors.dueDateTime = 'תאריך ושעה הם שדות חובה';
    } else {
      const dueDate = new Date(formData.dueDateTime);
      if (dueDate <= new Date()) {
        newErrors.dueDateTime = 'התאריך והשעה חייבים להיות בעתיד';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await reminderApi.createReminder(formData);
      onSave();
    } catch (err) {
      setErrors({ general: handleApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateReminderDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30); // At least 30 minutes from now
    // Format for datetime-local input
    return now.toISOString().slice(0, 16);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="תזכורת חדשה"
    >
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>כותרת <span>*</span></Label>
          <Input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="כותרת התזכורת"
            className={errors.title ? 'error' : ''}
            maxLength={200}
          />
          {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
        </FormGroup>

        <Row>
          <FormGroup>
            <Label>סוג תזכורת <span>*</span></Label>
            <Select
              value={formData.reminderType}
              onChange={(e) => handleChange('reminderType', parseInt(e.target.value))}
            >
              <option value={ReminderType.Call}>שיחה</option>
              <option value={ReminderType.FollowUp}>מעקב</option>
              <option value={ReminderType.Document}>מסמך</option>
              <option value={ReminderType.Meeting}>פגישה</option>
              <option value={ReminderType.Payment}>תשלום</option>
              <option value={ReminderType.Custom}>אחר</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>עדיפות</Label>
            <Select
              value={formData.priority}
              onChange={(e) => handleChange('priority', parseInt(e.target.value))}
            >
              <option value={ReminderPriority.High}>גבוהה</option>
              <option value={ReminderPriority.Medium}>בינונית</option>
              <option value={ReminderPriority.Low}>נמוכה</option>
            </Select>
          </FormGroup>
        </Row>

        <FormGroup>
          <StaffMultiPicker
            required
            value={formData.assignedUserIds}
            onChange={(userIds) => handleChange('assignedUserIds', userIds)}
            error={errors.assignedUserIds}
          />
        </FormGroup>

        <FormGroup>
          <Label>תאריך ושעה <span>*</span></Label>
          <Input
            type="datetime-local"
            value={formData.dueDateTime}
            onChange={(e) => handleChange('dueDateTime', e.target.value)}
            min={getMinDateTime()}
            className={errors.dueDateTime ? 'error' : ''}
          />
          {errors.dueDateTime && <ErrorMessage>{errors.dueDateTime}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>תיאור</Label>
          <TextArea
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="תיאור מפורט של התזכורת (אופציונלי)"
            maxLength={1000}
          />
          <HelpText>
            {formData.description?.length || 0}/1000 תווים
          </HelpText>
        </FormGroup>

        {errors.general && (
          <ErrorMessage>{errors.general}</ErrorMessage>
        )}

        <Actions>
          <Button type="button" onClick={onClose} disabled={loading}>
            ביטול
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'שומר...' : 'צור תזכורת'}
          </Button>
        </Actions>
      </Form>
    </Modal>
  );
};

export default CreateReminderModal;